/**
 * Full-page Prequalification cockpit (replaces drawer popup on the register).
 */
import React from 'react';
import { Alert, Button, Card, Space, Spin, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { EpcPrequalCockpit } from '@/components/epc/commercial-ux/EpcPrequalCockpit';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import { epcInvitationKind } from '@/constants/epcInvitationKind';
import { epcCommercialService } from '@/services/epcCommercial.service';

const { Title, Text } = Typography;

const EpcPrequalificationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const detailQ = useQuery({
    queryKey: ['epc-prequalification', id],
    queryFn: () => epcCommercialService.getPrequalification(String(id)),
    enabled: Boolean(id),
    staleTime: 10_000,
  });

  const row = (detailQ.data || null) as Record<string, unknown> | null;

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <Space align="start">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/epc/commercial/prequalifications')}
            aria-label="Back to prequalifications"
          />
          <div>
            <Title level={3} style={{ margin: 0 }}>
              {epcInvitationKind(row?.invitation_kind || row?.invitationKind) === 'EOI'
                ? 'Expression of Interest'
                : 'Prequalification cockpit'}
            </Title>
            <Text type="secondary">
              {row
                ? `${String(row.prequal_number || '')}${row.title ? ` — ${String(row.title)}` : ''}`
                : id
                  ? `ID ${id}`
                  : '—'}
              {epcInvitationKind(row?.invitation_kind || row?.invitationKind) === 'EOI'
                ? ' · Client invitation we respond to, not a supplier enquiry.'
                : ''}
            </Text>
          </div>
        </Space>
        {row?.id ? (
          <ReportDocumentActions
            reportKey="epc-eoi"
            params={{ id: String(row.id) }}
            documentId={String(row.id)}
            size="small"
            allowPreprinted
            sendPermissions={['epc.prequalification.manage', 'epc.admin']}
          />
        ) : null}
      </Space>

      {!id ? (
        <Alert type="error" showIcon message="Missing prequalification id" />
      ) : detailQ.isLoading ? (
        <Card>
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        </Card>
      ) : detailQ.isError || !row ? (
        <Alert
          type="error"
          showIcon
          message="Could not load prequalification"
          description={(detailQ.error as Error | null)?.message || 'Record not found'}
          action={
            <Button onClick={() => navigate('/epc/commercial/prequalifications')}>
              Back to list
            </Button>
          }
        />
      ) : (
        <Card>
          <EpcPrequalCockpit
            row={row}
            onUpdated={() => {
              void detailQ.refetch();
            }}
          />
        </Card>
      )}
    </div>
  );
};

export default EpcPrequalificationDetailPage;
